// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.

import React, { useState, useEffect, useRef } from 'react';
import BrandSelector from './components/BrandSelector';
import ModelSelector from './components/ModelSelector';
import ServiceSelector from './components/ServiceSelector';
import SchedulePicker from './components/SchedulePicker';
import AddressForm from './components/AddressForm';
import ReviewList from './components/ReviewList';
import VideoSection from './components/VideoSection';
import { models, timeSlots, reviews, repairServices as mockRepairServices } from './services/bookingService';
import { getBrands } from './services/brandService';
import { Brand, Model, RepairService, TimeSlot } from './types/BookingTypes';
import BookStepProcess from './components/BookStepProcess';
import CustomerFeedbackSlider from './components/CustomerFeedbackSlider';
import { getModelsByBrandId } from './services/modelService';
import { useNavigate, Link } from 'react-router-dom';
import { trackEvent, ClarityEvents } from './utils/clarityTracking';
import WhatAreYouLooking from './components/WhatAreYouLooking';
import LeadCapturePopup from './components/LeadCapturePopup';
import RecentRepairsCarousel from './components/RecentRepairsCarousel';
import HeroBanner from './components/HeroBanner';

const MobileRepairBookingPage: React.FC = () => {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedServices, setSelectedServices] = useState<RepairService[]>([]);
  const [repairServices, setRepairServices] = useState<RepairService[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [address, setAddress] = useState({ street: '', city: '', landmark: '', pincode: '' });
  const [activeSection, setActiveSection] = useState<string>('brand');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState<boolean>(true);
  const [brandsError, setBrandsError] = useState<string | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [repairPopupOpen, setRepairPopupOpen] = useState(false);
  const [pendingFeedbackMobile, setPendingFeedbackMobile] = useState<string | null>(null);
  const brandSectionRef = useRef<HTMLDivElement>(null);

  const filteredModels = selectedBrand
    ? models.filter(model => model.brandId === selectedBrand.id && model.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  useEffect(() => {
    // Track website visit
    trackEvent(ClarityEvents.WEBSITE_VISIT);
  }, []);

  const handleRequestFeedback = (cardMobile: string) => {
    setPendingFeedbackMobile(cardMobile);
    setRepairPopupOpen(true);
  };

  const handleFeedbackVerified = () => {
    setPendingFeedbackMobile(null);
    // popup stays open to show success screen — closed by Repair Now button
  };

  const handleRepairNow = () => {
    setRepairPopupOpen(false);
    setPendingFeedbackMobile(null);
    brandSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRepairPopupClose = () => {
    setRepairPopupOpen(false);
    setPendingFeedbackMobile(null);
  };

  const handleBrandSelect = (brand: Brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    setSelectedServices([]);
    
    // Track brand selection
    trackEvent(ClarityEvents.BRAND_SELECT, {
      brandId: brand.id,
      brandName: brand.name
    });
  };

  const handleModelSelect = (model: Model) => {
    setSelectedModel(model);
    setSelectedServices([]);
    
    // Track model selection
    trackEvent(ClarityEvents.MODEL_SELECT, {
      modelId: model.id,
      modelName: model.name,
      brandId: model.brandId
    });
  };

  const handleServiceSelect = (services: RepairService[]) => {
    setSelectedServices(services);
    
    // Track service selection
    const addedServices = services.filter(s => !selectedServices.find(ss => ss.id === s.id));
    const removedServices = selectedServices.filter(s => !services.find(ss => ss.id === s.id));
    
    addedServices.forEach(service => {
      trackEvent(ClarityEvents.SERVICE_ADD_TO_CART, {
        serviceId: service.id,
        serviceName: service.serviceName,
        modelId: selectedModel?.id,
        modelName: selectedModel?.name,
        price: service.actualPrice
      });
    });
    
    removedServices.forEach(service => {
      trackEvent(ClarityEvents.SERVICE_REMOVE_FROM_CART, {
        serviceId: service.id,
        serviceName: service.serviceName,
        modelId: selectedModel?.id,
        modelName: selectedModel?.name
      });
    });
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleTimeSlotSelect = (slot: TimeSlot) => {
    setSelectedTimeSlot(slot);
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddress(prev => ({ ...prev, [name]: value }));
  };

  const handleBookNow = () => {
    alert('Booking successful! A technician will visit you at the scheduled time.');
    setSelectedBrand(null);
    setSelectedModel(null);
    setSelectedServices([]);
    setSelectedDate('');
    setSelectedTimeSlot(null);
    setAddress({ street: '', city: '', landmark: '', pincode: '' });
    setActiveSection('brand');
  };

  const isBookingComplete = selectedBrand && selectedModel && selectedServices.length > 0 && selectedDate && selectedTimeSlot && address.street && address.city && address.pincode;

  // Generate dates for the next 7 days
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const formattedDate = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
      dates.push(formattedDate);
    }
    return dates;
  };
  const availableDates = generateDates();

  const handleModelBack = () => {
    setActiveSection('brand');
    setIsModelDropdownOpen(false);
  };

  useEffect(() => {
    setBrandsLoading(true);
    getBrands()
      .then(setBrands)
      .catch((err) => setBrandsError(err.message || 'Failed to load brands'))
      .finally(() => setBrandsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedBrand) return;
   
    setModelsLoading(true);
    setModelsError(null);
    getModelsByBrandId(selectedBrand.id)
      .then((data) => setModels(data))
      .catch((err) => setModelsError(err.message || 'Failed to load models'))
      .finally(() => setModelsLoading(false));
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedModel) return;
   
    // Load repair services for the selected model
    // For now, using mock data from bookingService
    setRepairServices(mockRepairServices);
  }, [selectedModel]);

  const navigate = useNavigate();

  const WhyChooseMinto = () => {
    const benefits = [
      {
        icon: "🛡️",
        title: "90 Days Warranty",
        description: "All spare parts come with 3 months warranty"
      },
      {
        icon: "🏃",
        title: "Flexible Service",
        description: "Doorstep or center visit - your choice!"
      },
      {
        icon: "⚡",
        title: "Quick Repair",
        description: "Most repairs done within 1 hour"
      },
      {
        icon: "🔧",
        title: "Genuine Parts",
        description: "100% authentic manufacturer parts"
      },
      {
        icon: "👨‍🔧",
        title: "Expert Technicians",
        description: "Skilled pros with 5+ years experience"
      },
      {
        icon: "💰",
        title: "No Fix No Fee",
        description: "Pay only after successful repair"
      }
    ];

  return (
      <div className="mb-6">
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden h-full">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-minto-500 rounded-full" />
              <h2 className="text-base md:text-lg font-semibold text-gray-900">Why Choose minto?</h2>
            </div>
          </div>
          <div>
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className={`flex items-center px-4 py-3 ${
                  index !== benefits.length - 1 ? 'border-b border-gray-50' : ''
                }`}
              >
                <div className="text-xl mr-3">{benefit.icon}</div>
                <div>
                  <h3 className="font-medium text-gray-800 text-sm">{benefit.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const [showReel, setShowReel] = useState(true);
  const [reelMuted, setReelMuted] = useState(true);
  const [reelLoaded, setReelLoaded] = useState(true);
  const reelVideoRef = useRef<HTMLVideoElement>(null);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <LeadCapturePopup />
      <LeadCapturePopup
        externalOpen={repairPopupOpen}
        onExternalClose={handleRepairPopupClose}
        onVerified={handleFeedbackVerified}
        onRepairNow={handleRepairNow}
        successMessage="Your request has been sent to the customer. They will reach out to share their feedback with you."
      />

      {/* Floating Reel — mobile only */}
      {showReel && (
        <div className="fixed bottom-24 left-4 z-40 block md:hidden" style={{ maxWidth: 'calc(100vw - 32px)' }}>
          <div className="relative w-28 sm:w-32 rounded-2xl overflow-hidden shadow-2xl border-2 border-white bg-black" style={{ aspectRatio: '9/16' }}>
            <video
              ref={reelVideoRef}
              src="https://mintorepairs.com/videos/minto-reel.mp4"
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />

            {/* Close button */}
            <button
              onClick={() => setShowReel(false)}
              className="absolute top-1.5 right-1.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
            >
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Mute / Unmute */}
            <button
              onClick={() => {
                if (reelVideoRef.current) {
                  reelVideoRef.current.muted = !reelVideoRef.current.muted;
                  setReelMuted(reelVideoRef.current.muted);
                }
              }}
              className="absolute bottom-8 right-1.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
            >
              {reelMuted ? (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16.5 12A4.5 4.5 0 0014 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06A8.99 8.99 0 0017.73 18L19 19.27 20.27 18 5.27 3 4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              ) : (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
              )}
            </button>

            {/* Fullscreen */}
            <button
              onClick={() => reelVideoRef.current?.requestFullscreen()}
              className="absolute bottom-1.5 right-1.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
            >
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
              </svg>
            </button>
          </div>
          <p className="text-center text-white text-[10px] mt-1 font-medium drop-shadow">Watch our reel ▶</p>
        </div>
      )}
      <div className="container mx-auto px-4 py-4 flex-grow">
        {/* Fixed Footer CTA - Home Page Only */}
        {activeSection === 'brand' && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3">
            <div className="flex items-center gap-3 max-w-lg mx-auto">
              <a
                href="tel:+919324299258"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-minto-300 text-minto-600 rounded-lg font-semibold text-sm hover:bg-minto-50 active:scale-95 transition-all"
              >
                <i className="fas fa-phone text-sm"></i>
                Call Us
              </a>
              <a
                href="https://wa.me/+919324299258?text=Hi,%20I%20need%20help%20with%20mobile%20repair%20service"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-minto-500 text-white rounded-lg font-semibold text-sm hover:bg-minto-600 active:scale-95 transition-all shadow-md"
              >
                <i className="fab fa-whatsapp text-base"></i>
                Chat with Us
              </a>
            </div>
          </div>
        )}
        
        {/* Home Banner */}
        <HeroBanner onBookNow={() => brandSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />

        {/* What are you looking for? */}
        <WhatAreYouLooking onSelect={() => brandSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />

        {/* Latest Repairs Done */}
        <RecentRepairsCarousel
          onRequestFeedback={handleRequestFeedback}
        />

        {/* Brand Selector + Happy Customers (side by side on desktop) */}
        <div className="flex flex-col md:flex-row md:items-start md:gap-4 mb-6" ref={brandSectionRef}>
          {/* Brand Selector - left on desktop */}
          <div className="md:flex-1">
            <BrandSelector brands={brands} selectedBrand={selectedBrand} />
          </div>

          {/* Happy Customers - right on desktop */}
          {activeSection === 'brand' && (
            <div className="hidden md:block md:w-60 md:flex-shrink-0">
              <CustomerFeedbackSlider />
            </div>
          )}
        </div>

        {/* Happy Customers - mobile only (below brands) */}
        {activeSection === 'brand' && (
          <div className="md:hidden">
            <CustomerFeedbackSlider />
          </div>
        )}

        {/* Professional Mobile Repair Section */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-minto-600 to-minto-800 p-5 text-white shadow-lg">
          <div className="relative z-10">
            <h1 className="text-2xl font-bold mb-2 leading-tight">Professional Mobile Repair</h1>
            <p className="text-minto-100 text-sm mb-4 leading-relaxed">Expert repairs at your doorstep</p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center bg-white/10 rounded-full px-3 py-1.5">
                <i className="fas fa-check-circle text-minto-200 mr-1.5 text-sm"></i>
                <span className="text-sm">Same Day Service</span>
              </div>
              <div className="flex items-center bg-white/10 rounded-full px-3 py-1.5">
                <i className="fas fa-shield-alt text-minto-200 mr-1.5 text-sm"></i>
                <span className="text-sm">90-Day Warranty</span>
              </div>
              <div className="flex items-center bg-white/10 rounded-full px-3 py-1.5">
                <i className="fas fa-tools text-minto-200 mr-1.5 text-sm"></i>
                <span className="text-sm">Certified Technicians</span>
              </div>
            </div>
          </div>
          <div className="absolute right-0 top-0 h-full w-1/3 opacity-10">
            <i className="fas fa-mobile-alt text-white text-[160px] absolute right-4 top-1/2 transform -translate-y-1/2"></i>
          </div>
        </div>

        {/* Model Selector (shown when brand is selected) */}
        {activeSection === 'model' && selectedBrand && (
          modelsLoading ? (
            <div className="text-center py-8 text-lg">Loading models...</div>
          ) : modelsError ? (
            <div className="text-center py-8 text-red-500 text-lg">{modelsError}</div>
          ) : (
            <ModelSelector
              models={models.filter(model => model.name.toLowerCase().includes(searchQuery.toLowerCase()))}
              selectedModel={selectedModel}
              onSelect={handleModelSelect}
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              onBack={() => setActiveSection('brand')}
              brandName={selectedBrand.name}
            />
          )
        )}

        {/* Service Selector (shown when model is selected) */}
        {activeSection === 'service' && selectedModel && (
          <ServiceSelector
            services={repairServices}
            selectedServices={selectedServices}
            onSelect={handleServiceSelect}
            model={selectedModel}
            modelName={selectedModel.name}
            onBack={() => setActiveSection('model')}
          />
        )}

        {/* Schedule Picker (shown when services are selected) */}
        {activeSection === 'schedule' && selectedServices.length > 0 && (
          <div className="space-y-6">
            <SchedulePicker
              dates={availableDates}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              timeSlots={timeSlots}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={handleTimeSlotSelect}
            />
            {selectedTimeSlot && (
              <AddressForm
                address={address}
                onChange={handleAddressChange}
                onSubmit={handleBookNow}
                disabled={!isBookingComplete}
              />
            )}
          </div>
        )}

        {/* Supporting Sections (only shown on home/brand selection page) */}
        {activeSection === 'brand' && (
          <div className="pb-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 md:items-stretch">
              <div className="order-2 md:order-1 flex">
                <div className="w-full">
                  <VideoSection />
                </div>
              </div>
              <div className="order-1 md:order-2 flex">
                <div className="w-full">
                  <WhyChooseMinto />
                </div>
              </div>
            </div>
            <ReviewList reviews={reviews} isLoading={false} />

            {/* SEO Content Section */}
            <div className="mt-12 bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-1.5 mb-4">
                <i className="fas fa-info-circle text-minto-500"></i>
                <h2 className="text-lg font-semibold text-gray-900">About Us</h2>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-3">
                Mobile Repair at doorstep in Kalyan, Dombivli & Palava City, Thane, Mumbai
              </h1>
              <h2 className="text-lg font-medium text-gray-700 mb-4">
                Quick, Affordable, and Trusted Doorstep Mobile Repairs
              </h2>
              <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
                <p>
                  Welcome to minto - your trusted partner for professional mobile repair services at your doorstep in Kalyan, Dombivli, and Palava City. We understand the inconvenience of a malfunctioning smartphone and bring expert repair solutions directly to your home.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-800 mb-2">Expert Mobile Repair Services</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      <li>Screen replacement and repairs</li>
                      <li>Battery replacement and optimization</li>
                      <li>Camera and speaker repairs</li>
                      <li>Water damage treatment</li>
                      <li>Software troubleshooting</li>
                      <li>Charging port repairs</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-800 mb-2">Why Choose Our Doorstep Service?</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      <li>Convenient home repair service</li>
                      <li>90-day repair warranty</li>
                      <li>Genuine spare parts</li>
                      <li>Transparent pricing</li>
                      <li>Expert technicians</li>
                      <li>Same-day service available</li>
                    </ul>
                  </div>
                </div>
                <p>
                  Our mobile repair service covers all major brands including Samsung, Apple, OnePlus, Xiaomi, Oppo, Vivo, and more. Whether you're in Kalyan, Dombivli, or Palava City, our skilled technicians are just a call away to provide professional repair services at your convenience.
                </p>
                <p>
                  Experience hassle-free mobile repairs with our doorstep service. We use only genuine parts, offer competitive prices, and ensure complete satisfaction with our 90-day warranty. From screen replacements to battery issues, our certified technicians handle all repairs with expertise and care.
                </p>
                <div className="mt-4 text-xs text-gray-500">
                  <p>
                    Service Areas: Kalyan East, Kalyan West, Dombivli East, Dombivli West, Palava City Phase 1, Palava City Phase 2, and surrounding areas.
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Brand by Prajapati Technologies Pvt. Ltd.</p>
                    <p>Registered Office: Shop No 2 Vijeta Apartment, Near Thakurli Railway Station, Thakurli (E) 421201</p>
                    <p>CIN: U74999MH2017PTC300063</p>
                    <p>GST: 27AAJCP4476F1Z4</p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex flex-wrap gap-4 text-xs">
                      <Link to="/about" className="text-minto-600 hover:text-minto-700 hover:underline">
                        About Us
                      </Link>
                      <Link to="/refunds-cancellations" className="text-minto-600 hover:text-minto-700 hover:underline">
                        Refunds & Cancellations
                      </Link>
                      <Link to="/terms-conditions" className="text-minto-600 hover:text-minto-700 hover:underline">
                        Terms & Conditions
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer with Cart Summary */}
      {(selectedModel || selectedServices.length > 0) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.1)] border-t border-gray-200">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div 
                onClick={() => navigate('/cart')}
                className="flex items-center cursor-pointer group"
              >
                {selectedModel && (
                  <div className="w-10 h-10 rounded-lg bg-gray-50 p-1 mr-3 flex items-center justify-center">
                    <img
                      src={selectedModel.image}
                      alt={selectedModel.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <div className="flex items-center">
                  <i className="fas fa-shopping-cart text-gray-400 text-lg mr-2"></i>
                  <span className="text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
                    {selectedServices.length} {selectedServices.length === 1 ? 'Item' : 'Items'}
                  </span>
                  <i className="fas fa-chevron-right text-gray-400 text-xs ml-1.5 group-hover:text-blue-600 transition-colors"></i>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href="https://wa.me/+919876543210?text=Hi,%20I%20need%20help%20with%20mobile%20repair%20service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2.5 rounded-xl font-medium text-base text-white bg-green-500 hover:bg-green-600 active:bg-green-700 transition-colors shadow-md hover:shadow-lg flex items-center"
                >
                  <i className="fab fa-whatsapp text-lg mr-2"></i>
                  Chat With Us
                </a>
                <button
                  onClick={() => navigate('/cart')}
                  className="px-6 py-2.5 rounded-xl font-medium text-base text-white bg-minto-500 hover:bg-minto-600 active:bg-minto-700 transition-colors shadow-md hover:shadow-lg"
                >
                  View Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileRepairBookingPage;